/**
 * MudMixer Savings Calculator — v3.1
 * Monthly framing | Business growth positioning
 * 6 inputs → 4 outputs + hero counter + compare strip + sticky bar
 *
 * HARDCODED CONSTANTS (confirmed April 9, 16 & 30 calls with Peyton):
 *   Manual mixing baseline : 25 bags/hr
 *   Evolution output rate  : 45 bags/hr
 *   Pro output rate        : 135 bags/hr (3× Evolution)
 *   1 cu yd                : 45 × 80lb bags
 *   Evolution machine price: $3,695
 *   Pro machine price      : $5,995 (introductory pre-order price)
 *   Evolution operators    : 1
 *   Pro operators          : 2 (Pro requires 2-person operation)
 *
 * v3.1 additions:
 *   • Animated counter on hero "+jobs" with bump animation
 *   • Slider track gradient fill (amber up to thumb)
 *   • Payback date display ("Paid off by Jul 2026")
 *   • Payback timeline bar (% of 12 months)
 *   • "Already spending" callout (loss-framed)
 *   • Compare strip (Evolution vs Pro at current inputs) — collapsible
 *   • Sticky bottom bar (model · jobs · paid-off-by · savings · CTA)
 *   • Dynamic CTA text ("Unlock +X More Jobs / Month")
 */

(function () {
  'use strict';

  // ─── CONSTANTS ───────────────────────────────────────────────────────────────

  var MANUAL_RATE   = 25;                              // bags/hr — confirmed April 30
  var MODEL_RATE    = { evolution: 45, pro: 135 };
  var MACHINE_PRICE = { evolution: 3695, pro: 5995 };  // Pro: introductory pre-order
  var OPERATORS     = { evolution: 1, pro: 2 };        // Pro requires 2-person operation
  var BAGS_PER_CUYD = 45;                              // 80lb bags per cubic yard

  var MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  var MONTHS_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  // ─── STATE ───────────────────────────────────────────────────────────────────

  var currentModel       = 'evolution';
  var jobSizeUnit        = 'cuyd';
  var prevAdditionalJobs = 8;
  var counterAnimId      = null;
  var compareOpen        = false;

  // ─── PRESETS ─────────────────────────────────────────────────────────────────

  var PRESETS = {
    fencePost:      { jobSize: 0.5, unit: 'cuyd', crewSize: 2, jobsPerMonth: 15 },
    footers:        { jobSize: 1,   unit: 'cuyd', crewSize: 2, jobsPerMonth: 12 },
    foundationSlab: { jobSize: 3,   unit: 'cuyd', crewSize: 3, jobsPerMonth: 6  },
    deckPatio:      { jobSize: 2,   unit: 'cuyd', crewSize: 2, jobsPerMonth: 8  },
    commercialUse:  { jobSize: 4,   unit: 'cuyd', crewSize: 5, jobsPerMonth: 20 },
  };

  // ─── FORMULA ─────────────────────────────────────────────────────────────────

  function calculate(inputs, model) {
    var rate      = MODEL_RATE[model];
    var price     = MACHINE_PRICE[model];
    var operators = OPERATORS[model];

    var bags = (inputs.jobSizeUnit === 'cuyd')
      ? inputs.jobSize * BAGS_PER_CUYD
      : inputs.jobSize;

    var manualTimePerJob = bags / MANUAL_RATE;
    var mixerTimePerJob  = bags / rate;

    var monthlyTimeSaved = (manualTimePerJob - mixerTimePerJob) * inputs.jobsPerMonth;

    var manualLaborPerJob   = inputs.crewSize * inputs.laborRate * manualTimePerJob;
    var mixerLaborPerJob    = operators       * inputs.laborRate * mixerTimePerJob;
    var monthlyLaborSavings = (manualLaborPerJob - mixerLaborPerJob) * inputs.jobsPerMonth;

    var additionalJobs   = (mixerTimePerJob > 0)
      ? Math.max(0, Math.floor(monthlyTimeSaved / mixerTimePerJob))
      : 0;

    var totalMonthlySavings = monthlyLaborSavings + inputs.rentalCost + inputs.readyMixFees;
    var paybackMonths = (totalMonthlySavings > 0) ? price / totalMonthlySavings : Infinity;
    var paybackJobs   = isFinite(paybackMonths) ? Math.round(paybackMonths * inputs.jobsPerMonth) : 0;

    return {
      monthlyLaborSavings : monthlyLaborSavings,
      additionalJobs      : additionalJobs,
      monthlyTimeSaved    : monthlyTimeSaved,
      totalMonthlySavings : totalMonthlySavings,
      paybackMonths       : paybackMonths,
      paybackJobs         : paybackJobs,
    };
  }

  // ─── INPUT READING ───────────────────────────────────────────────────────────

  function getInputs() {
    return {
      laborRate    : parseFloat(val('sc-labor'))         || 25,
      jobSize      : parseFloat(val('sc-jobSize'))       || 2,
      jobSizeUnit  : jobSizeUnit,
      crewSize     : parseInt(val('sc-workers'), 10)     || 2,
      jobsPerMonth : parseInt(val('sc-jobsPerMonth'),10) || 10,
      rentalCost   : parseFloat(val('sc-rental'))        || 0,
      readyMixFees : parseFloat(val('sc-readyMix'))      || 0,
    };
  }

  function val(id) {
    var el = document.getElementById(id);
    return el ? el.value : '';
  }

  // ─── HELPERS ─────────────────────────────────────────────────────────────────

  function fmt$(n) { return '$' + Math.round(Math.max(0, n)).toLocaleString(); }
  function fmt1(n) { return Math.max(0, n).toFixed(1); }

  function set(id, text) {
    var el = document.getElementById(id);
    if (el) el.textContent = text;
  }

  function setHTML(id, html) {
    var el = document.getElementById(id);
    if (el) el.innerHTML = html;
  }

  function payDate(months, short) {
    if (!isFinite(months) || months <= 0) return '—';
    var d = new Date();
    d.setMonth(d.getMonth() + Math.ceil(months));
    return (short ? MONTHS_SHORT : MONTHS_LONG)[d.getMonth()] + ' ' + d.getFullYear();
  }

  function ordinal(n) {
    var s = ['th', 'st', 'nd', 'rd'];
    var v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  // ─── ANIMATED COUNTER (hero +jobs) ───────────────────────────────────────────

  function animateCounter(from, to, el) {
    if (counterAnimId) cancelAnimationFrame(counterAnimId);
    var t0 = performance.now();
    var dur = 420;
    el.classList.remove('bump');

    function step(now) {
      var p = Math.min(1, (now - t0) / dur);
      var e = 1 - Math.pow(1 - p, 3); // easeOutCubic
      el.textContent = '+' + Math.round(from + (to - from) * e);
      if (p < 1) {
        counterAnimId = requestAnimationFrame(step);
      } else {
        el.textContent = '+' + to;
        // Force reflow so the bump animation re-triggers each render
        void el.offsetWidth;
        el.classList.add('bump');
      }
    }
    counterAnimId = requestAnimationFrame(step);
  }

  // ─── SLIDER TRACK FILL ───────────────────────────────────────────────────────

  function fillSlider(el) {
    var min = parseFloat(el.min);
    var max = parseFloat(el.max);
    var v   = parseFloat(el.value);
    var pct = ((v - min) / (max - min)) * 100;
    el.style.background = 'linear-gradient(to right, #f5b400 ' + pct + '%, #e7e7e7 ' + pct + '%)';
  }

  function fillAllSliders() {
    document.querySelectorAll('.sc-slider').forEach(fillSlider);
  }

  // ─── COMPARE STRIP TOGGLE ────────────────────────────────────────────────────

  function toggleCompare() {
    compareOpen = !compareOpen;
    var body = document.getElementById('sc-compare-body');
    var btn  = document.getElementById('sc-compare-btn');
    var hd   = document.querySelector('.sc-compare-hd');
    if (body) body.style.display = compareOpen ? 'block' : 'none';
    if (btn)  btn.textContent = compareOpen ? 'Hide ↑' : 'Show ↓';
    if (hd)   hd.setAttribute('aria-expanded', compareOpen ? 'true' : 'false');
  }

  // ─── OUTPUT RENDERING ────────────────────────────────────────────────────────

  function renderOutputs(results, inputs) {
    // Hero — animated counter
    var heroEl = document.getElementById('sc-additionalJobs');
    if (heroEl) {
      animateCounter(prevAdditionalJobs, results.additionalJobs, heroEl);
      prevAdditionalJobs = results.additionalJobs;
    }

    // Output cards
    set('sc-monthlyLaborSavings', fmt$(results.monthlyLaborSavings));
    set('sc-timeSaved',           fmt1(results.monthlyTimeSaved));
    set('sc-totalMonthlySavings', fmt$(results.totalMonthlySavings));

    // Payback period + date + timeline bar
    var timelineFill = document.getElementById('sc-timeline-fill');
    var hasWorkaroundCost = (inputs.rentalCost + inputs.readyMixFees) > 0;
    if (!isFinite(results.paybackMonths) || results.totalMonthlySavings <= 0) {
      set('sc-paybackPeriod', 'N/A');
      set('sc-paybackSub',    'Enter your inputs to calculate payback');
      set('sc-paybackDate',   '');
      if (timelineFill) timelineFill.style.width = '0%';
    } else {
      var periodText = results.paybackMonths < 1 ? '<1 month' : fmt1(results.paybackMonths) + ' mo';
      set('sc-paybackPeriod', periodText);
      set('sc-paybackSub',
        hasWorkaroundCost
          ? 'Pays for itself in ' + results.paybackJobs + ' jobs'
          : 'Based on labor savings only — add rental or ready-mix spend for full ROI');
      set('sc-paybackDate',   'Paid off by ' + payDate(results.paybackMonths, false));
      if (timelineFill) timelineFill.style.width = Math.min(100, (results.paybackMonths / 12) * 100) + '%';
    }

    // "Already spending" callout (loss framing)
    var workaround = inputs.rentalCost + inputs.readyMixFees;
    var calloutEl  = document.getElementById('sc-callout');
    if (calloutEl) {
      if (workaround > 0) {
        calloutEl.style.display = 'flex';
        var cycles = isFinite(results.paybackMonths) ? Math.ceil(results.paybackMonths) : null;
        var html = "You're already spending <strong>" + fmt$(workaround) + "</strong>/month on workarounds. ";
        html += cycles
          ? "MudMixer pays for itself before your <strong>" + ordinal(cycles) + "</strong> rental cycle."
          : "Add rental or ready-mix amounts to see your payback timeline.";
        setHTML('sc-callout-text', html);
      } else {
        calloutEl.style.display = 'none';
      }
    }

    // Compare strip — calculate both models at current inputs
    var evo = calculate(inputs, 'evolution');
    var pro = calculate(inputs, 'pro');
    set('cmp-evo-jobs',    '+' + evo.additionalJobs);
    set('cmp-evo-labor',   fmt$(evo.monthlyLaborSavings));
    set('cmp-evo-payback', isFinite(evo.paybackMonths) ? fmt1(evo.paybackMonths) + ' mo' : 'N/A');
    set('cmp-pro-jobs',    '+' + pro.additionalJobs);
    set('cmp-pro-labor',   fmt$(pro.monthlyLaborSavings));
    set('cmp-pro-payback', isFinite(pro.paybackMonths) ? fmt1(pro.paybackMonths) + ' mo' : 'N/A');
    set('cmp-mult', evo.additionalJobs > 0
      ? (pro.additionalJobs / evo.additionalJobs).toFixed(1) + '×'
      : '—');

    // Sticky bar
    set('sticky-model',   currentModel === 'pro' ? 'MudMixer Pro' : 'Evolution');
    set('sticky-jobs',    '+' + results.additionalJobs);
    set('sticky-savings', fmt$(results.totalMonthlySavings));
    set('sticky-date',
      isFinite(results.paybackMonths) && results.totalMonthlySavings > 0
        ? payDate(results.paybackMonths, true)
        : '—');

    // Dynamic CTA text
    set('sc-cta-text', 'Unlock +' + results.additionalJobs + ' More Jobs / Month');
  }

  // ─── MODEL TOGGLE ────────────────────────────────────────────────────────────

  function setSavingsModel(model, btn) {
    currentModel = model;

    document.querySelectorAll('.sc-model-btn').forEach(function (b) {
      b.classList.remove('active');
    });
    if (btn) btn.classList.add('active');

    var price = fmt$(MACHINE_PRICE[model]);
    set('sc-machinePrice',  price);
    set('sc-machinePrice2', price);

    recalculate();
  }

  // ─── JOB SIZE UNIT TOGGLE ────────────────────────────────────────────────────

  function setJobSizeUnit(unit, btn) {
    if (unit === jobSizeUnit) return;

    var slider = document.getElementById('sc-jobSize');
    var lim    = document.getElementById('sc-jsize-limits');
    if (!slider) return;

    var currentVal = parseFloat(slider.value) || 0;

    if (unit === 'bags') {
      jobSizeUnit  = 'bags';
      slider.min   = 1;
      slider.max   = 360;
      slider.step  = 1;
      slider.value = Math.round(currentVal * BAGS_PER_CUYD);
      if (lim) lim.innerHTML = '<span>1 bag</span><span>360 bags</span>';
    } else {
      jobSizeUnit  = 'cuyd';
      slider.min   = 0.5;
      slider.max   = 8;
      slider.step  = 0.5;
      slider.value = (Math.round((currentVal / BAGS_PER_CUYD) * 2) / 2).toFixed(1);
      if (lim) lim.innerHTML = '<span>0.5 cu yd</span><span>8 cu yd</span>';
    }

    document.querySelectorAll('.sc-unit-btn').forEach(function (b) {
      b.classList.toggle('active', b.dataset.unit === unit);
    });

    updateJobSizeDisplay();
    fillSlider(slider);
    recalculate();
  }

  function updateJobSizeDisplay() {
    var slider    = document.getElementById('sc-jobSize');
    var displayEl = document.getElementById('sc-jobSizeDisplay');
    if (!slider || !displayEl) return;

    var v = parseFloat(slider.value) || 0;
    if (jobSizeUnit === 'cuyd') {
      var bags = Math.round(v * BAGS_PER_CUYD);
      displayEl.textContent = v + ' cu yd (' + bags + ' bags)';
    } else {
      var cuyd = (v / BAGS_PER_CUYD).toFixed(1);
      displayEl.textContent = v + ' bags (' + cuyd + ' cu yd)';
    }
  }

  // ─── PRESETS ─────────────────────────────────────────────────────────────────

  function loadPreset(presetKey) {
    var p = PRESETS[presetKey];
    if (!p) return;

    if (p.unit !== jobSizeUnit) {
      jobSizeUnit = p.unit;
      var slider = document.getElementById('sc-jobSize');
      var lim    = document.getElementById('sc-jsize-limits');
      if (slider) {
        if (p.unit === 'bags') {
          slider.min = 1; slider.max = 360; slider.step = 1;
          if (lim) lim.innerHTML = '<span>1 bag</span><span>360 bags</span>';
        } else {
          slider.min = 0.5; slider.max = 8; slider.step = 0.5;
          if (lim) lim.innerHTML = '<span>0.5 cu yd</span><span>8 cu yd</span>';
        }
      }
      document.querySelectorAll('.sc-unit-btn').forEach(function (b) {
        b.classList.toggle('active', b.dataset.unit === p.unit);
      });
    }

    setSliderValue('sc-jobSize',      p.jobSize);
    setSliderValue('sc-workers',      p.crewSize);
    setSliderValue('sc-jobsPerMonth', p.jobsPerMonth);

    updateJobSizeDisplay();
    updateAllDisplayValues();
    fillAllSliders();
    recalculate();

    document.querySelectorAll('.sc-preset-btn').forEach(function (b) {
      b.classList.toggle('active', b.dataset.preset === presetKey);
    });
  }

  function setSliderValue(id, v) {
    var el = document.getElementById(id);
    if (el) {
      el.value = v;
      updateDisplayValue(el);
    }
  }

  // ─── SLIDER DISPLAY ──────────────────────────────────────────────────────────

  function updateDisplayValue(input) {
    var displayEl = document.getElementById(input.id + '-display');
    if (!displayEl) return;

    var v       = parseFloat(input.value) || 0;
    var prefix  = input.dataset.prefix  || '';
    var suffix  = input.dataset.suffix  || '';
    var decimal = input.dataset.decimal ? parseInt(input.dataset.decimal, 10) : 0;

    var formatted = decimal > 0 ? v.toFixed(decimal) : Math.round(v).toLocaleString();
    displayEl.textContent = prefix + formatted + suffix;
  }

  function updateAllDisplayValues() {
    document.querySelectorAll('.sc-slider').forEach(function (el) {
      if (el.id !== 'sc-jobSize') updateDisplayValue(el);
    });
    updateJobSizeDisplay();
  }

  // ─── RECALCULATE ─────────────────────────────────────────────────────────────

  function recalculate() {
    var inputs  = getInputs();
    var results = calculate(inputs, currentModel);
    renderOutputs(results, inputs);
  }

  // ─── INIT ────────────────────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('.sc-slider').forEach(function (slider) {
      slider.addEventListener('input', function () {
        if (this.id === 'sc-jobSize') {
          updateJobSizeDisplay();
        } else {
          updateDisplayValue(this);
        }
        fillSlider(this);
        document.querySelectorAll('.sc-preset-btn').forEach(function (b) {
          b.classList.remove('active');
        });
        recalculate();
      });
    });

    document.querySelectorAll('.sc-model-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setSavingsModel(this.dataset.model, this);
      });
    });

    document.querySelectorAll('.sc-unit-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setJobSizeUnit(this.dataset.unit, this);
      });
    });

    document.querySelectorAll('.sc-preset-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        loadPreset(this.dataset.preset);
      });
    });

    // Compare strip — clicking the header bar (or its toggle button) toggles
    var compareHd = document.querySelector('.sc-compare-hd');
    if (compareHd) {
      compareHd.addEventListener('click', toggleCompare);
      compareHd.addEventListener('keydown', function (e) {
        if (e.target !== compareHd) return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          toggleCompare();
        }
      });
    }

    // Initial render
    updateAllDisplayValues();
    updateJobSizeDisplay();
    fillAllSliders();
    recalculate();

    // Default active states
    var defaultModelBtn = document.querySelector('.sc-model-btn[data-model="evolution"]');
    if (defaultModelBtn) defaultModelBtn.classList.add('active');

    var defaultUnitBtn = document.querySelector('.sc-unit-btn[data-unit="cuyd"]');
    if (defaultUnitBtn) defaultUnitBtn.classList.add('active');
  }

  // ─── GLOBAL EXPOSE ───────────────────────────────────────────────────────────

  window.setSavingsModel  = setSavingsModel;
  window.setJobSizeUnit   = setJobSizeUnit;
  window.loadPreset       = loadPreset;
  window.toggleCompare    = toggleCompare;
  window.mudmixerCalcInit = init;

  // ─── BOOT ────────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
